import { Injectable } from '@angular/core';
import { HttpInterceptor, HttpRequest, HttpHandler, HttpEvent, HttpHeaders } from '@angular/common/http';
import { Observable, from, switchMap, of } from 'rxjs';
import { environment } from '../../environments/environment';
import { AppStateService } from '../app-state.service';
import { HttpClient } from '@angular/common/http';

@Injectable()
export class AnonymousAuthInterceptor implements HttpInterceptor {
  private anonymousToken: string | null = null;
  private tokenExpiry: number | null = null;

  constructor(
    private http: HttpClient,
    private appStateService: AppStateService
  ) {}

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    // Skip if request already has authentication (x-api-key or Authorization header)
    // This allows services to handle their own auth (like signup with x-api-key)
    if (req.headers.has('x-api-key') || req.headers.has('Authorization')) {
      return next.handle(req);
    }

    // Check if this is a signup-related endpoint that needs anonymous token
    const needsAnonymousAuth = this.needsAnonymousToken(req.url);
    console.log('[AnonymousAuth] URL:', req.url, '| needsAnonymousAuth:', needsAnonymousAuth, '| hasValidToken:', this.isTokenValid());

    if (!needsAnonymousAuth) {
      return next.handle(req);
    }

    // If we have a valid token, use it
    if (this.isTokenValid()) {
      console.log('[AnonymousAuth] Using cached token for:', req.url);
      const authReq = req.clone({
        headers: req.headers.set('Authorization', `Bearer ${this.anonymousToken}`)
      });
      return next.handle(authReq);
    }

    // Otherwise, get a new anonymous token first
    return from(this.getAnonymousToken()).pipe(
      switchMap(token => {
        if (token) {
          const authReq = req.clone({
            headers: req.headers.set('Authorization', `Bearer ${token}`)
          });
          return next.handle(authReq);
        }
        // If we couldn't get a token, proceed without auth header
        return next.handle(req);
      })
    );
  }

  private needsAnonymousToken(url: string): boolean {
    // List of endpoints that need anonymous Bearer token (via token exchange)
    const anonymousEndpoints = [
      '/patients/family_lookup',
      '/patients/create',
      '/patients/register',
      '/forms/templates',
      '/patients/',  // Covers /patients/{id}/forms/* endpoints
      '/kiosk/activate-device',
      '/kiosk/verify-pin',
    ];

    // Check if URL matches any anonymous endpoint
    // But exclude endpoints that have their own auth (like /patients/profile which needs real auth)
    const excludeEndpoints = [
      '/patients/profile/'
    ];

    if (excludeEndpoints.some(endpoint => url.includes(endpoint))) {
      return false;
    }

    return anonymousEndpoints.some(endpoint => url.includes(endpoint));
  }

  private isTokenValid(): boolean {
    if (!this.anonymousToken || !this.tokenExpiry) {
      return false;
    }
    // Check if token is still valid (with 5 minute buffer)
    return Date.now() < (this.tokenExpiry - 5 * 60 * 1000);
  }

  private async getAnonymousToken(): Promise<string | null> {
    try {
      const config = this.appStateService.getState();
      const clientId = config.clientId || environment.clientId || 'tlnk-brightsmiledental-tx';
      const orgId = config.orgId || environment.orgId || 'tlnk-def-1f3ce';

      console.log('[AnonymousAuth] Getting anonymous token for:', { clientId, orgId });

      const anonTokenData = {
        id_token: 'anonymous',
        org_id: orgId,
        client_id: clientId,
        patient_id: 'anonymous'
      };

      // Construct tenant-specific API URL dynamically
      let apiUrl = environment.apiUrl;
      
      // First try to get tenant from app state
      let orgTenantId = config.orgTenantId;
      
      // If not available, extract from current hostname as fallback
      if (!orgTenantId) {
        const hostname = window.location.hostname;
        if (hostname !== 'localhost' && hostname !== '127.0.0.1') {
          orgTenantId = hostname.split('.')[0];
        }
      }
      
      if (orgTenantId) {
        apiUrl = apiUrl.replace('https://', `https://${orgTenantId}.`);
      }

      // Include x-api-key header for token exchange
      const headers = new HttpHeaders({
        'Content-Type': 'application/json',
        'x-api-key': environment.apiKey
      });

      const response = await this.http.post<any>(
        `${apiUrl}/auth/exchange_anon_token`,
        anonTokenData,
        { headers }
      ).toPromise();

      if (response?.access_token) {
        this.anonymousToken = response.access_token;
        // Assume token is valid for 1 hour
        this.tokenExpiry = Date.now() + 60 * 60 * 1000;
        console.log('[AnonymousAuth] Token obtained successfully');
        return this.anonymousToken;
      }
      console.warn('[AnonymousAuth] No access_token in response:', response);
    } catch (error) {
      console.error('[AnonymousAuth] Failed to get token:', error);
    }
    
    return null;
  }

  // Method to clear cached token (useful for testing or refresh)
  clearToken(): void {
    this.anonymousToken = null;
    this.tokenExpiry = null;
  }
}